import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import { LayoutLoading } from 'components/LayoutLoading'
import { feePercent, renderStripeAmountDetails, Stripe } from 'components/Stripe'
import { UsioCheckout } from 'components/Usio'
import { LoanProcessRushPaymentRequestTypeTitles } from 'pages/LoanSubmission/constants'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  type IMakePaymentData,
  getActivePaymentProvider,
  getUsioConvenienceFee,
  PaymentOrderType,
  PaymentProvider,
  StripeIntentPaidStatus,
  StripeIntentStatus,
  StripeMethod,
  updatePaymentStatus,
} from 'services/apis/stripe'
import { ButtonGroup } from 'stories/components'
import { getPrice2decimal } from 'utils'

// Same wording as the servicing phone-payment modal, so staff and payers see one set of choices.
// Built on call, not at module load, so an import cycle through `services` cannot crash startup.
const methodTitles = (): Record<string, string> => ({
  [StripeMethod.card]: 'Credit / Debit Card',
  [StripeMethod.us_bank_account]: 'ACH (Bank Account)',
  [StripeMethod.pinless_debit]: 'Pinless Debit',
})

export const StripeWithMethod = ({
  availableMethods = [StripeMethod.card, StripeMethod.us_bank_account],
  defaultMethod,
  paymentData,
  paymentDetails,
  dataForMethod,
  onMethodChange,
  onCompleted,
}: {
  availableMethods?: StripeMethod[]
  /** Method to start on (and follow when it changes), for a page that already asked which one. */
  defaultMethod?: StripeMethod
  paymentData: IMakePaymentData
  paymentDetails?: Record<string, any>
  /**
   * Extra payment data that depends on the method picked here, merged in when the payment is
   * created. It has to be resolved at that moment: a parent that learns the method from
   * `onMethodChange` only re-renders after the checkout has already created the intent.
   */
  dataForMethod?: (method: StripeMethod) => Record<string, any>
  onMethodChange?: (method: StripeMethod) => void
  onCompleted: Function
}) => {
  const rulesConfig = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.RulesConfig] || {})
  const configuredProvider =
    rulesConfig.paymentProvider === PaymentProvider.usio ? PaymentProvider.usio : PaymentProvider.stripe

  const [defaultProvider, setDefaultProvider] = useState<PaymentProvider>(configuredProvider)
  // Credit card and ACH can each run through a different provider per area, so the checkout follows
  // whichever method the payer picks. Pinless debit only exists at USIO.
  const [methodProviders, setMethodProviders] = useState<Partial<Record<StripeMethod, PaymentProvider>>>({})
  const providerFor = (m?: StripeMethod) =>
    m === StripeMethod.pinless_debit ? PaymentProvider.usio : (m && methodProviders[m]) || defaultProvider
  // The active provider is confirmed from the backend before any checkout widget mounts. Contexts
  // that don't hydrate RulesConfig into redux (e.g. the borrower portal) would otherwise seed the
  // Stripe default and mount the Stripe widget on a USIO tenant, failing with "Stripe isn't set up".
  const [providerResolved, setProviderResolved] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [method, setMethod] = useState<StripeMethod | undefined>(
    availableMethods.length == 1
      ? availableMethods[0]
      : defaultMethod && availableMethods.includes(defaultMethod)
      ? defaultMethod
      : undefined,
  )
  const pickMethod = (m: StripeMethod) => {
    setMethod(m)
    onMethodChange?.(m)
  }

  // Follow the page's own method field when the payer changes it there instead of here.
  useEffect(() => {
    if (defaultMethod && availableMethods.includes(defaultMethod)) setMethod(defaultMethod)
  }, [defaultMethod])
  const [status, setStatus] = useState(paymentDetails ? paymentDetails.status : '')
  // USIO returns its own convenience fee per amount + method (flat/tiered), which is the correct
  // figure instead of the Stripe-style hardcoded percentage. Re-fetched when the method changes.
  const [usioFee, setUsioFee] = useState(0)
  // The tenant's own fee on top of USIO's (Rules Configuration > Payment Methods). It is charged and
  // shown as part of the convenience fee, but only `usioFee` is declared to USIO.
  const [additionalFee, setAdditionalFee] = useState(0)
  const totalFee = Number((usioFee + additionalFee).toFixed(2))
  // A card fee is priced from the card number, so it cannot be quoted until the payer has typed one
  // into the checkout below; the checkout hands it up once it is complete. A six-digit prefix is not
  // enough — USIO answers that with success and a 0.00 fee, which would quote the payer no fee at
  // all and then be rejected against the merchant's fee minimum when the charge is priced for real.
  const [cardNumberForFee, setCardNumberForFee] = useState('')
  const [feeNote, setFeeNote] = useState('')
  // The gateway product the fee was quoted under, which the charge then has to run through. USIO
  // prices a debit card at a pinless-enabled tenant under its pinless schedule, a different product
  // from the card one, so the intent has to be created under whichever the quote resolved to.
  const [chargeMethod, setChargeMethod] = useState<StripeMethod | undefined>(undefined)

  const provider = providerFor(method)

  useEffect(() => {
    getActivePaymentProvider(paymentData.orderType)
      .then((res) => {
        if (res?.provider) setDefaultProvider(res.provider)
        const byMethod: Partial<Record<StripeMethod, PaymentProvider>> = {}
        Object.entries(res?.methods || {}).forEach(([m, item]) => {
          if (item?.provider) byMethod[m as StripeMethod] = item.provider
        })
        setMethodProviders(byMethod)
      })
      .catch(() => {})
      // On failure, fall back to the redux-configured provider rather than blocking the payment UI.
      .finally(() => setProviderResolved(true))
  }, [])

  // Switching payment type invalidates whatever card was half-typed under the previous one.
  useEffect(() => setCardNumberForFee(''), [method])

  const isAchMethod = method === StripeMethod.us_bank_account

  useEffect(() => {
    // A quote still in flight when the payer switches method must not land on the new one: an ACH
    // answer arriving after they switched back to card would flip the charge (and the form) to ACH
    // under the card button.
    let cancelled = false
    setFeeNote('')
    setChargeMethod(method)
    if (provider !== PaymentProvider.usio || !method || !paymentData.amount) {
      setUsioFee(0)
      setAdditionalFee(0)
      return
    }
    // Asking with a partial number comes back with no fee at all, which would then be charged as a
    // $0 fee and rejected against the merchant's fee minimum. Wait for the full number instead.
    if (!isAchMethod && !cardNumberForFee) {
      setUsioFee(0)
      setAdditionalFee(0)
      setFeeNote('Enter your card number below to see the fee')
      return
    }
    getUsioConvenienceFee({
      amount: paymentData.amount,
      method,
      accountNumber: isAchMethod ? undefined : cardNumberForFee,
      orderType: paymentData.orderType,
    })
      .then((res) => {
        if (cancelled) return
        if (res?.ok === false) {
          setUsioFee(0)
          setAdditionalFee(0)
          setFeeNote(res?.message || 'The convenience fee could not be quoted')
          return
        }
        if (res?.method) setChargeMethod(res.method)
        setUsioFee(Number(res?.fee) || 0)
        setAdditionalFee(Number(res?.additionalFee) || 0)
      })
      .catch(() => {
        if (cancelled) return
        setUsioFee(0)
        setAdditionalFee(0)
        setFeeNote('The convenience fee could not be quoted')
      })
    return () => {
      cancelled = true
    }
  }, [provider, method, paymentData.amount, paymentData.orderType, cardNumberForFee, isAchMethod])

  const strTitle = useMemo(() => {
    if (![PaymentOrderType.LoanSubmissionRush, PaymentOrderType.CounterPartyApproval].includes(paymentData.orderType))
      return ''
    if (paymentData.orderType == PaymentOrderType.RentalLedger) return paymentData.title
    return LoanProcessRushPaymentRequestTypeTitles[paymentData.requestType] || paymentData.feeTitle
  }, [paymentData])

  // Pinless debit is USIO's way of running a card, so it is offered only where this area's cards go
  // to USIO.
  const cardProvider = providerFor(StripeMethod.card)
  const visibleMethods = useMemo(() => {
    if (cardProvider !== PaymentProvider.usio) {
      return availableMethods.filter((m) => m !== StripeMethod.pinless_debit)
    }
    return availableMethods
  }, [availableMethods, cardProvider])

  const methodTitleMap = useMemo(() => {
    const map: Record<string, string> = {}
    visibleMethods.forEach((m) => {
      map[m] = methodTitles()[m]
    })
    return map
  }, [visibleMethods])

  const isShowBankLink = useMemo(() => {
    return visibleMethods.includes(StripeMethod.us_bank_account) && method == StripeMethod.card
  }, [visibleMethods, method])

  const isShowCardLink = useMemo(() => {
    return visibleMethods.includes(StripeMethod.card) && method == StripeMethod.us_bank_account
  }, [visibleMethods, method])

  const onPayCompleted = async (intentId: string) => {
    setLoading(true)

    updatePaymentStatus(intentId)
      .then(({ status, message }) => {
        setStatus(status)
        onCompleted(intentId, status, message)
      })
      .finally(() => setLoading(false))
  }

  const statusTxt = useMemo(() => {
    if (!paymentDetails) return ''
    switch (paymentDetails.status) {
      case StripeIntentStatus.payment_failed:
      case StripeIntentStatus.requires_payment_method:
        return 'Failed'
      case StripeIntentStatus.succeeded:
        return 'Success'
    }
    return 'Processing'
  }, [paymentDetails])

  const isPaid = status && StripeIntentPaidStatus.includes(status)

  // What a Stripe payment charges: the amount plus the checkout's surcharge for the method.
  const stripeCharged = Number(Number(paymentData.amount * (1 + (method ? feePercent[method] : 0))).toFixed(2))

  const feeKey =
    method === StripeMethod.pinless_debit ? StripeMethod.card : method

  return (
    <div>
      <LayoutLoading show={isLoading} />

      {isPaid && (
        <div
          className="my-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative text-[15px] flex items-center"
          role="alert"
        >
          <CheckCircleIcon className="w-6 h-6"></CheckCircleIcon>
          <span className="ml-1">
            {status == StripeIntentStatus.succeeded
              ? 'Successfully Paid.'
              : `We've received your payment request. This will be completed in 1~2 days.`}
          </span>
        </div>
      )}

      {paymentDetails &&
        paymentDetails.intentId &&
        [StripeIntentStatus.payment_failed, StripeIntentStatus.requires_payment_method].includes(status) &&
        !!paymentDetails.message && (
          <div
            className="my-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-[15px] flex items-center"
            role="alert"
          >
            <XCircleIcon className="w-6 h-6"></XCircleIcon>
            <span className="ml-1">{paymentDetails.message}</span>
          </div>
        )}

      {paymentData && (
        <div className="flex items-center gap-2 mb-4">
          <p>Email:</p>
          <p className="text-lg text-black font-semibold">{paymentData?.email}</p>
        </div>
      )}

      {paymentData && strTitle && (
        <div className="flex items-center gap-2 mb-4">
          <p>Type:</p>
          <p className="text-lg text-black font-semibold">{strTitle}</p>
        </div>
      )}

      {!!status &&
        paymentDetails &&
        paymentDetails.intentId &&
        !!paymentData.email &&
        paymentData.email == paymentData?.email && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <p>Payment Status:</p>
              <p className="text-lg text-black font-semibold">{statusTxt}</p>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <p>Payment Intent:</p>
              <p className="text-lg text-black font-semibold">{paymentDetails.intentId}</p>
            </div>
            {paymentDetails.provider && (
              <div className="flex items-center gap-2 mb-4">
                <p>Provider:</p>
                <p className="text-lg text-black font-semibold capitalize">{paymentDetails.provider}</p>
              </div>
            )}
          </>
        )}

      {provider === PaymentProvider.usio ? (
        <div className="grid grid-cols-2 mb-8 text-[14.5px] items-end">
          <p>Payment Amount:</p>
          <p className="text-lg text-black">$ {getPrice2decimal(paymentData.amount, false, true)}</p>
          <p className="border-b border-gray-300 pb-2 mb-2">Convenience Fee:</p>
          <p className="border-b border-gray-300 pb-2 mb-2 text-lg text-black">
            {totalFee ? (
              `$ ${getPrice2decimal(totalFee, false, true)}`
            ) : (
              <span className="text-[13px] text-gray-500">{feeNote || '-'}</span>
            )}
          </p>
          <p>Total:</p>
          <p className="text-lg text-black font-bold">
            {totalFee || !feeNote
              ? `$ ${getPrice2decimal(Number((paymentData.amount + totalFee).toFixed(2)), false, true)}`
              : '-'}
          </p>
        </div>
      ) : (
        renderStripeAmountDetails(paymentData.amount, feeKey)
      )}

      {!isPaid && !providerResolved && (
        <div className="py-6 text-gray-500 text-[15px]">Loading payment options…</div>
      )}

      {!isPaid && providerResolved && (
        <>
          {visibleMethods.length > 1 && (
            <div className="flex items-center gap-2 mb-4">
              <p className="mb-4">Payment Type:</p>
              <ButtonGroup title={methodTitleMap} value={method} className="mb-4" onChange={(v) => pickMethod(v)} />
            </div>
          )}

          {provider === PaymentProvider.usio ? (
            <UsioCheckout
              key={method}
              orderType={paymentData.orderType as any}
              refId={paymentData.refId}
              email={paymentData.email}
              amount={Number((paymentData.amount + totalFee).toFixed(2))}
              // The product the quote resolved to, so the intent is created -- and charged -- on the
              // same fee schedule the payer was just shown. Deliberately NOT the `key` above: that
              // still tracks the button they pressed, or resolving the BIN would remount the form
              // and wipe the card number that triggered the resolution.
              method={(chargeMethod || method) as any}
              // The fee has to travel with the payment, not just sit in this screen's total: the
              // backend declares it to USIO as ConvFeeAmount (a fee-program merchant rejects a
              // charge that declares none) and credits the loan `appliedAmount`, not the total.
              data={{
                ...paymentData,
                ...(method && dataForMethod ? dataForMethod(method) : {}),
                appliedAmount: paymentData.amount,
                convenienceFee: Number(usioFee.toFixed(2)),
                // The backend works this out again from the config and pins the charged amount to it.
                additionalFee,
              }}
              setLoading={setLoading}
              onCompleted={onPayCompleted}
              onCardNumberChange={setCardNumberForFee}
              lenderName={paymentData.lenderName}
            />
          ) : (
            <Stripe
              key={method}
              orderType={paymentData.orderType as any}
              refId={paymentData.refId}
              email={paymentData.email}
              amount={stripeCharged}
              method={method as any}
              // Record the split the same way the USIO path does: the loan is credited `appliedAmount`,
              // and the surcharge on top is the convenience fee. Without it the borrower-payment queue
              // saw only the charged total, which never matched the amount due.
              data={{
                ...paymentData,
                ...(method && dataForMethod ? dataForMethod(method) : {}),
                appliedAmount: paymentData.amount,
                convenienceFee: Number((stripeCharged - paymentData.amount).toFixed(2)),
              }}
              setLoading={setLoading}
              onCompleted={onPayCompleted}
              lenderName={paymentData.lenderName}
            />
          )}

          {isShowBankLink && (
            <p className="mt-2">
              - If you want to pay via ACH from your bank account,{' '}
              <span
                className="font-semibold text-shade-blue cursor-pointer hover:underline inline text-base"
                onClick={() => pickMethod(StripeMethod.us_bank_account)}
              >
                Click Here
              </span>
              !
            </p>
          )}

          {isShowCardLink && (
            <p className="mt-2">
              - If you want to pay with Credit Card,{' '}
              <span
                className="font-semibold text-shade-blue cursor-pointer hover:underline inline text-base"
                onClick={() => pickMethod(StripeMethod.card)}
              >
                Click Here
              </span>
              !
            </p>
          )}
        </>
      )}
    </div>
  )
}
